import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
} from 'motion/react';

export interface SwipePagerProps {
  /** 目前應顯示的頁。由外部狀態決定，本元件只負責把轉場演完 */
  pageKey: string;
  /**
   * 回傳相鄰頁的 key（`step` 為 1 是下一頁、-1 是上一頁），例如日期的前後一天。
   * 要是穩定的參考（模組層級的函式），否則每次渲染都會重新掛手勢監聽。
   */
  getAdjacentKey: (pageKey: string, step: 1 | -1) => string;
  /** 滑動換頁時回報新的頁。外部更新後會透過 pageKey 傳回來，由本元件接續動畫 */
  onPage: (nextPageKey: string) => void;
  /** 是否啟用滑動。桌面應停用：滑鼠拖曳的語意是選取文字 */
  enabled: boolean;
  /** 渲染指定頁的內容。相鄰兩頁會被一併渲染以備滑動 */
  renderPage: (pageKey: string) => ReactNode;
  /**
   * 手勢的接收範圍。預設為軌道本身。
   *
   * 軌道只有內容那麼高，內容底下的空白處收不到手勢，但使用者的認知是「整片
   * 主內容都能翻頁」。傳入外層的捲動容器即可把整片區域納入，同時把不在容器內的
   * 部分（例如頂部橫幅）排除在外。
   */
  gestureSurfaceRef?: RefObject<HTMLElement | null>;
}

/** 換頁動畫。導覽動作要快，使用者的目的是看到下一頁的內容，不是看動畫 */
const PAGE_TRANSITION = { duration: 0.28, ease: 'easeOut' } as const;

/** 位移超過容器寬度的此比例即換頁。四分之一是分頁介面的常見手感 */
const PAGE_DISTANCE_RATIO = 0.25;

/** 快速輕掃即使距離不足也換頁（px/s） */
const PAGE_VELOCITY = 400;

/**
 * 快掃仍須走過的最短距離（px）。
 *
 * 只看速度會誤判：垂直捲動時手指軌跡難免帶一點水平分量，瞬間速度輕易就超過門檻。
 * 要求同時走過一小段距離，才能區分「真的想翻頁」與「捲動的殘影」。
 */
const PAGE_MIN_OFFSET = 16;

/**
 * 判定主軸前要先累積的位移（px）。
 *
 * 太小會被手指落下時的微顫誤導，太大則起手遲鈍——手指走了一段畫面才跟上。
 */
const AXIS_LOCK_THRESHOLD = 10;

/**
 * 判定為「橫向」的最大斜率 |dy| / |dx|。
 *
 * 1 相當於 45° 的分界。放寬到 1.2（約 50°）是刻意偏袒橫向：翻頁是使用者特地做的
 * 動作，手勢自然帶有弧度；捲動則是長距離的持續移動，即使起手斜也會很快站回垂直，
 * 不太會落在這個錐形內。
 */
const HORIZONTAL_SLOPE = 1.2;

/**
 * 速度取樣的最小間隔（ms）。
 *
 * 相鄰兩個 touchmove 可能只差幾毫秒，直接相除會把抖動放大成假的高速。取相隔一段
 * 時間的舊點當基準，速度才穩定。
 */
const VELOCITY_SAMPLE_INTERVAL = 40;

/** 進行中的滑動手勢 */
interface SwipeState {
  /** 手指起點。位移一律相對於此，畫面才會貼著手指走 */
  startX: number;
  startY: number;
  /** 主軸判定結果。null 表示位移還不足以判定 */
  axis: 'x' | 'y' | null;
  /** 最新的水平位移（已夾在可拖範圍內），放手時據以決定是否換頁 */
  offsetX: number;
  /** 手指最新的水平座標 */
  lastX: number;
  /** 速度取樣的基準點：與最新位置相隔至少一個取樣間隔的舊點 */
  sampleX: number;
  sampleTime: number;
}

/**
 * 左右滑動換頁的軌道容器，用在逐日、逐月這類一頁接一頁的檢視。
 *
 * 採原生分頁介面的軌道結構：上一頁／目前頁／下一頁排成一條軌道，手指直接拖動軌道，
 * 放開後從手指離開的位置吸附到目標格。全程只有一個位移量在動，拖曳與換頁動畫無縫銜接。
 * 吸附完成後把資料視窗推進一格、軌道歸零，新的中間格與停下的那格內容相同，畫面不跳動。
 *
 * 非滑動來源的換頁（橫幅上的箭頭等）走同一條路徑：外部把 `pageKey` 改成相鄰頁時，
 * 演同一段吸附動畫。
 *
 * 手勢自行監聽 touch 事件，不用 motion 的 `drag`：`dragDirectionLock` 只看垂直位移
 * 是否先超過門檻，帶一點垂直分量的橫掃就會被判成捲動。這裡以**夾角**判定主軸
 * （見 `HORIZONTAL_SLOPE`），判定為橫向後在非被動的 touchmove 上 `preventDefault`；
 * 捲動只能在瀏覽器開始捲之前擋下，所以判定必須在手勢的前幾個 move 內完成。
 *
 * 只在觸控裝置啟用（`enabled`），滑鼠拖曳的語意是選取文字。
 */
export function SwipePager({
  pageKey,
  getAdjacentKey,
  onPage,
  enabled,
  renderPage,
  gestureSurfaceRef,
}: SwipePagerProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const swipeRef = useRef<SwipeState | null>(null);
  const x = useMotionValue(0);
  const prefersReducedMotion = useReducedMotion();

  /* 實際渲染中的中心頁。轉場期間刻意落後 pageKey 一步，等動畫演完才追上 */
  const [shownKey, setShownKey] = useState(pageKey);
  const [viewportWidth, setViewportWidth] = useState(0);

  useLayoutEffect(() => {
    const element = viewportRef.current;
    if (!element) return;

    const observer = new ResizeObserver(([entry]) => {
      setViewportWidth(entry.contentRect.width);
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  /* pageKey 變了就把軌道吸附過去，然後把資料視窗推過去、軌道歸零 */
  useEffect(() => {
    if (pageKey === shownKey) return;

    const delta = _adjacentDelta(shownKey, pageKey, getAdjacentKey);

    /*
     * 不相鄰（例如從月曆跳到上週）沒有中間格可演，直接換；
     * 減少動態時同理。
     *
     * 即使不需要動畫也走 `animate`（duration 0）而非直接 setState：在 effect 內
     * 同步 setState 會觸發連鎖渲染，改由 onComplete 回呼更新，兩條路徑也因此
     * 共用同一段收尾邏輯。
     */
    const skipAnimation =
      delta === 0 || prefersReducedMotion || viewportWidth === 0;

    const controls = animate(x, skipAnimation ? 0 : -delta * viewportWidth, {
      ...PAGE_TRANSITION,
      duration: skipAnimation ? 0 : PAGE_TRANSITION.duration,
      onComplete: () => {
        setShownKey(pageKey);
        x.set(0);
      },
    });

    return () => controls.stop();
  }, [
    pageKey,
    shownKey,
    getAdjacentKey,
    viewportWidth,
    prefersReducedMotion,
    x,
  ]);

  useEffect(() => {
    const element = gestureSurfaceRef?.current ?? viewportRef.current;
    if (!element || !enabled) return;

    const handleTouchStart = (event: TouchEvent) => {
      /*
       * 多指、或轉場動畫還在演（pageKey 已變、shownKey 未追上）時不接手勢：
       * 前者是縮放，後者接下去會與 animate 搶同一個位移量。
       */
      if (event.touches.length !== 1 || pageKey !== shownKey) {
        swipeRef.current = null;
        return;
      }

      const touch = event.touches[0];
      swipeRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        axis: null,
        offsetX: 0,
        lastX: touch.clientX,
        sampleX: touch.clientX,
        sampleTime: event.timeStamp,
      };
    };

    const handleTouchMove = (event: TouchEvent) => {
      const swipe = swipeRef.current;
      if (!swipe) return;

      const touch = event.touches[0];
      const offsetX = touch.clientX - swipe.startX;
      const offsetY = touch.clientY - swipe.startY;

      if (swipe.axis === null) {
        if (
          Math.max(Math.abs(offsetX), Math.abs(offsetY)) < AXIS_LOCK_THRESHOLD
        )
          return;
        swipe.axis =
          Math.abs(offsetY) <= Math.abs(offsetX) * HORIZONTAL_SLOPE ? 'x' : 'y';
      }

      /* 判定為捲動：整段手勢都交回瀏覽器，中途不再改判 */
      if (swipe.axis !== 'x') return;

      /*
       * 鎖定為橫向後每一個 move 都要擋。只擋判定當下那一次不夠——瀏覽器會在後續
       * 任何未被取消的 move 上重新開始捲動。
       */
      event.preventDefault();

      /* 最多拖到相鄰的一格，再過去沒有內容可看 */
      swipe.offsetX = Math.max(
        -viewportWidth,
        Math.min(viewportWidth, offsetX),
      );
      x.set(swipe.offsetX);

      /* 基準點超過一個取樣間隔才往前推，維持「最新位置與基準相隔夠久」的不變量 */
      if (event.timeStamp - swipe.sampleTime >= VELOCITY_SAMPLE_INTERVAL) {
        swipe.sampleX = swipe.lastX;
        swipe.sampleTime = event.timeStamp;
      }
      swipe.lastX = touch.clientX;
    };

    const handleTouchEnd = (event: TouchEvent) => {
      const swipe = swipeRef.current;
      swipeRef.current = null;
      if (!swipe || swipe.axis !== 'x') return;

      const { offsetX } = swipe;

      /*
       * 速度以「放手的時刻」而非最後一次 move 為終點計算。手指移到一半停住幾百
       * 毫秒再放開時，分母會被拉長、速度自然趨近零，不會拿停住前的殘值當快掃。
       */
      const elapsed = event.timeStamp - swipe.sampleTime;
      const velocityX =
        elapsed > 0 ? ((swipe.lastX - swipe.sampleX) / elapsed) * 1000 : 0;

      const draggedFarEnough =
        Math.abs(offsetX) > viewportWidth * PAGE_DISTANCE_RATIO;
      const flicked =
        Math.abs(velocityX) > PAGE_VELOCITY &&
        Math.abs(offsetX) > PAGE_MIN_OFFSET;

      if (!draggedFarEnough && !flicked) {
        /* 未過門檻：吸附回原位，與換頁共用同一組曲線 */
        animate(x, 0, PAGE_TRANSITION);
        return;
      }

      /* 內容向左推 → 露出右邊的下一頁，與箭頭鍵「右 = 下一頁」的方向一致 */
      onPage(getAdjacentKey(shownKey, offsetX < 0 ? 1 : -1));
    };

    const handleTouchCancel = () => {
      const swipe = swipeRef.current;
      swipeRef.current = null;
      if (swipe?.axis === 'x') animate(x, 0, PAGE_TRANSITION);
    };

    /* touchmove 必須是非被動的，passive listener 內的 preventDefault 會被忽略 */
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd);
    element.addEventListener('touchcancel', handleTouchCancel);

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [
    enabled,
    viewportWidth,
    shownKey,
    pageKey,
    getAdjacentKey,
    onPage,
    x,
    gestureSurfaceRef,
  ]);

  return (
    /*
      橫向裁掉軌道溢出的相鄰兩頁，縱向保持 visible：相鄰頁若比目前頁長，
      在滑動途中不該被攔腰切掉。`clip` 是唯一能與 `visible` 併用的裁切值：
      換成 `hidden` 會讓另一軸被迫變成 auto，多出一條捲軸。
    */
    <div ref={viewportRef} className="overflow-x-clip overflow-y-visible">
      <motion.div
        /*
         * 預設把垂直捲動交回瀏覽器：判定為捲動的手勢不必等 JS 決定就能滑順捲。
         * 判定為橫向時，touchmove 的 preventDefault 會蓋過這個許可。
         */
        style={{ x, touchAction: enabled ? 'pan-y' : undefined }}
        className="relative"
      >
        {/*
          相鄰兩頁絕對定位在軌道左右各一個身位，不進入版面流。

          這是「換頁時畫面不會上下抽動」的關鍵：三格若並排成 flex row，軌道高度
          會是三頁中最高的那頁，而換頁後資料視窗推進一格、最大值換人，高度就在
          吸附完成的瞬間跳動。改為絕對定位後，軌道高度永遠只由中間那格決定，
          與相鄰兩頁的長短無關。

          `right-full`／`left-full` 相對於軌道自身寬度（=一格），因此不必再為軌道
          補基準位移；`top-0` 讓相鄰兩頁與目前頁的頂端對齊，滑動時視線落點不變。
        */}
        <div className="absolute top-0 right-full w-full">
          {renderPage(getAdjacentKey(shownKey, -1))}
        </div>

        {/* 只有這一格在版面流內，軌道的高度就是它的高度 */}
        <div>{renderPage(shownKey)}</div>

        <div className="absolute top-0 left-full w-full">
          {renderPage(getAdjacentKey(shownKey, 1))}
        </div>
      </motion.div>
    </div>
  );
}

/** 兩頁是否相鄰，回傳 1（下一頁）、-1（上一頁）或 0（不相鄰）。 */
function _adjacentDelta(
  from: string,
  to: string,
  getAdjacentKey: SwipePagerProps['getAdjacentKey'],
): -1 | 0 | 1 {
  if (getAdjacentKey(from, 1) === to) return 1;
  if (getAdjacentKey(from, -1) === to) return -1;
  return 0;
}
